import { computed, ref, watch, type Ref } from 'vue'
import { useSubscriptionState } from './useSubscriptionState'
import { startDate, duration, stayEnd, type QuoteLine } from './useSubscription'
import {
  fetchAvailableBundles,
  computePrice,
  type Bundle,
  type AvailableBundlesParams,
  type ComputePriceParams,
  type ContractInput,
} from '@/services/subscription'
import { ApiError } from '@/services/http'
import { subscriptionFlowFor, type PriceTag } from '@/subscriptionFlows'

/**
 * Backend-sourced data for a subscription flow, generic over the product.
 *
 * Everything product-specific (subcategory, answer → API params, plan ↔ bundle
 * mapping, cross-sell, top-up, period) is read from the active flow definition
 * (`subscriptionFlowFor`); this composable owns only the generic machinery:
 * fetching + de-duplicating requests, the route-derived view models, and the
 * compute_price totals. A product with no flow definition stays inert (empty
 * results) so it keeps using the front-computed pricing path.
 *
 * Fetched state lives at module level (shared across every component on the page)
 * and requests are de-duplicated by their query, so the results screen and the
 * quote panel trigger a single request between them.
 */

/** A bundle adapted for the results screen: API data paired with the plan slug. */
export type SubscriptionFlowProduct = {
  tierKey: string
  bundleId: string
  /** Backend bundle key (i18n key for the translated label), or null when absent. */
  bundleKey: string | null
  recommended: boolean
  /** Price in euros, or null when the API omits it. */
  pricePerMonth: number | null
  pricePerYear: number | null
}

/** A cross-sell add-on derived from a base bundle's upsell (price = difference). */
export type CrossSellAddon = {
  key: string
  /** i18n key (in the `bundles` namespace) for the add-on's translated label. */
  labelKey: string
  pricePerMonth: number
  pricePerYear: number
}

// ---- Shared fetched state (module-level singleton) -----------------------
const bundles = ref<Bundle[]>([])
const loading = ref(false)
/** i18n key of the current bundles-list error, or null. */
const error = ref<string | null>(null)

/** Top-up bundle (e.g. Tonic-Études), fetched only when the top-up applies. */
const topUpBundle = ref<Bundle | null>(null)

// De-dup keys: the query currently loaded/in-flight, so concurrent consumers
// don't fire the same request twice.
let loadedBundlesKey: string | null = null
let loadedTopUpKey: string | null = null
let bundlesController: AbortController | null = null
let topUpController: AbortController | null = null

const round2 = (n: number): number => Math.round(n * 100) / 100

/** Fetch the bundles for `params`, unless that exact query is already loaded. */
async function ensureBundles(params: AvailableBundlesParams | null): Promise<void> {
  const key = params ? JSON.stringify(params) : null
  if (key === loadedBundlesKey) return
  loadedBundlesKey = key

  if (!params || typeof window === 'undefined') {
    bundles.value = []
    error.value = null
    return
  }

  bundlesController?.abort()
  bundlesController = new AbortController()
  loading.value = true
  error.value = null
  try {
    bundles.value = await fetchAvailableBundles(params, bundlesController.signal)
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') return
    bundles.value = []
    error.value =
      e instanceof ApiError && e.status === 422
        ? 'subscription.results.error.noBundles'
        : 'subscription.results.error.load'
    loadedBundlesKey = null // allow a retry of the same query
  } finally {
    loading.value = false
  }
}

/** Fetch the top-up bundle once, when it becomes relevant. */
async function ensureTopUp(config: { subcategory: AvailableBundlesParams['subcategory']; reference: number } | null): Promise<void> {
  const key = config ? JSON.stringify(config) : null
  if (key === loadedTopUpKey) return
  loadedTopUpKey = key

  if (!config || typeof window === 'undefined') {
    topUpBundle.value = null
    return
  }

  topUpController?.abort()
  topUpController = new AbortController()
  try {
    const list = await fetchAvailableBundles({ subcategory: config.subcategory }, topUpController.signal)
    topUpBundle.value = list.find((b) => b.referenceMysmerra === config.reference) ?? list[0] ?? null
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') return
    topUpBundle.value = null
    loadedTopUpKey = null // allow a retry
  }
}

/** A compute_price result slot (one backend-computed total), de-duplicated by query. */
type PriceSlot = {
  amount: Ref<number | null>
  loading: Ref<boolean>
  error: Ref<string | null>
  ensure: (params: ComputePriceParams | null) => Promise<void>
}

function createPriceSlot(): PriceSlot {
  const amount = ref<number | null>(null)
  const slotLoading = ref(false)
  const slotError = ref<string | null>(null)
  let loadedKey: string | null = null
  let controller: AbortController | null = null

  async function ensure(params: ComputePriceParams | null): Promise<void> {
    const key = params ? JSON.stringify(params) : null
    if (key === loadedKey) return
    loadedKey = key

    if (!params || typeof window === 'undefined') {
      amount.value = null
      slotError.value = null
      return
    }

    controller?.abort()
    controller = new AbortController()
    slotLoading.value = true
    slotError.value = null
    try {
      amount.value = await computePrice(params, controller.signal)
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return
      amount.value = null
      slotError.value = 'subscription.quote.priceError'
      loadedKey = null
    } finally {
      slotLoading.value = false
    }
  }

  return { amount, loading: slotLoading, error: slotError, ensure }
}

// One slot for the main formula (base or combined upsell), one for the top-up.
const planTotal = createPriceSlot()
const topUpTotal = createPriceSlot()

export function useSubscriptionFlow() {
  const { answers, product } = useSubscriptionState()

  /** Active flow definition for the current product, or null when not wired. */
  const flow = computed(() => subscriptionFlowFor(product.value))

  /** True when the current product is backed by the API (health, abroad…); the
   *  pricing/quote consumers use it to source totals from the flow rather than
   *  the front-computed estimate in useSubscription. */
  const hasFlow = computed(() => flow.value !== null)

  // available_bundles params, or null until the flow's inputs are all answered.
  const bundlesParams = computed<AvailableBundlesParams | null>(
    () => flow.value?.bundlesParams(answers.value) ?? null,
  )

  // The subscription period (inclusive dates) from the shared form state.
  const period = computed(() =>
    flow.value?.period({
      startDate: startDate.value,
      duration: duration.value,
      stayEnd: stayEnd.value,
    }) ?? null,
  )

  // Formules for the results screen, ordered by coverage. Bundles with no known
  // plan mapping are dropped.
  const products = computed<SubscriptionFlowProduct[]>(() => {
    const f = flow.value
    if (!f) return []
    const source = f.recommendedOnly ? bundles.value.filter((b) => b.recommended) : bundles.value
    return source
      .map((b): SubscriptionFlowProduct | null => {
        const tierKey = b.referenceMysmerra != null ? f.planKeyOf(b.referenceMysmerra) : null
        if (!tierKey) return null
        const price = f.displayPrice(b)
        return {
          tierKey,
          bundleId: b.id,
          bundleKey: b.key,
          recommended: b.recommended,
          pricePerMonth: price.perMonth,
          pricePerYear: price.perYear,
        }
      })
      .filter((p): p is SubscriptionFlowProduct => p !== null)
      .sort((a, b) => f.planReference[a.tierKey] - f.planReference[b.tierKey])
  })

  // Base bundle (with its upsells) matching the chosen plan slug.
  const selectedBaseBundle = computed<Bundle | null>(() => {
    const f = flow.value
    const plan = answers.value.plan
    if (!f || !plan) return null
    const reference = f.planReference[plan]
    return bundles.value.find((b) => b.referenceMysmerra === reference) ?? null
  })

  // Cross-sell add-ons for the chosen formula, priced as upsell − base.
  // Add-ons whose upsell or price is missing are dropped (hidden in the UI).
  const crossSellAddons = computed<CrossSellAddon[]>(() => {
    const f = flow.value
    const base = selectedBaseBundle.value
    if (!f || !f.crossSell || !base || base.monthlyPrice == null || base.annualPrice == null) return []
    const out: CrossSellAddon[] = []
    for (const upsell of base.upsellBundles) {
      const key = f.crossSellValueOf(upsell)
      if (!key || upsell.monthlyPrice == null || upsell.annualPrice == null) continue
      out.push({
        key,
        labelKey: f.crossSellLabelKey(upsell),
        pricePerMonth: round2(upsell.monthlyPrice - base.monthlyPrice),
        pricePerYear: round2(upsell.annualPrice - base.annualPrice),
      })
    }
    return out.sort((a, b) => f.crossSellOrder(a.key) - f.crossSellOrder(b.key))
  })

  // Top-up applies only when the chosen plan offers it (flow.topUp.isEnabled).
  const topUpEnabled = computed(() => {
    const f = flow.value
    return !!f?.topUp && f.topUp.isEnabled(answers.value)
  })
  const topUpSubscribed = computed(() => flow.value?.isTopUpSubscribed(answers.value) ?? false)
  const topUpConfig = computed(() => {
    const f = flow.value
    return topUpEnabled.value && f?.topUp
      ? { subcategory: f.topUp.subcategory, reference: f.topUp.reference }
      : null
  })

  /** Standard price of the chosen formula (base bundle), for recap cards. */
  const selectedPlanPrice = computed<PriceTag | null>(() => {
    const f = flow.value
    const b = selectedBaseBundle.value
    return f && b ? f.displayPrice(b) : null
  })

  /** Standard top-up price, for the top-up screen and recap. */
  const tonicPrice = computed<PriceTag | null>(() => {
    const b = topUpBundle.value
    return b ? { perMonth: b.monthlyPrice, perYear: b.annualPrice } : null
  })

  /** i18n key (in the `bundles` namespace) for the top-up bundle's label, or
   *  null until it is loaded (the UI then falls back to its hard-coded name). */
  const tonicLabelKey = computed<string | null>(() => topUpBundle.value?.key ?? null)

  // Quote breakdown (plan + topUp + crossSell), all priced from the API.
  const lines = computed<QuoteLine[]>(() => {
    const f = flow.value
    const out: QuoteLine[] = []
    const base = selectedBaseBundle.value
    const plan = answers.value.plan
    const planPrice = f && base ? f.displayPrice(base) : null
    if (plan && base && planPrice && planPrice.perMonth != null && planPrice.perYear != null) {
      out.push({
        kind: 'plan',
        value: plan,
        labelKey: base.key ?? undefined,
        perMonth: planPrice.perMonth,
        perYear: planPrice.perYear,
      })
    }
    const topUp = topUpBundle.value
    if (topUpSubscribed.value && topUp && topUp.monthlyPrice != null && topUp.annualPrice != null) {
      out.push({
        kind: 'topUp',
        value: 'tonic',
        labelKey: topUp.key ?? undefined,
        perMonth: topUp.monthlyPrice,
        perYear: topUp.annualPrice,
      })
    }
    const cs = f?.crossSell ? answers.value[f.crossSell.answerKey] : undefined
    if (cs === 'security' || cs === 'indispensable') {
      const addon = crossSellAddons.value.find((a) => a.key === cs)
      if (addon) {
        out.push({
          kind: 'crossSell',
          value: cs,
          labelKey: addon.labelKey,
          perMonth: addon.pricePerMonth,
          perYear: addon.pricePerYear,
        })
        // Pack + assurance: the "Assurance Examen" is offered (included, 0 €).
        if (f?.crossSell?.bundlesExamInsurance) {
          out.push({ kind: 'examInsurance', value: 'exam', perMonth: 0, perYear: 0 })
        }
      }
    }
    return out
  })

  const monthlyTotal = computed(() => round2(lines.value.reduce((sum, l) => sum + l.perMonth, 0)))
  const yearlyTotal = computed(() => round2(lines.value.reduce((sum, l) => sum + l.perYear, 0)))

  // The bundle the user ends up subscribing to: the combined upsell when an
  // add-on is chosen, otherwise the base formula. Source of truth for both the
  // compute_price id and the recap label.
  const selectedBundle = computed<Bundle | null>(() => {
    const f = flow.value
    const base = selectedBaseBundle.value
    if (!f || !base) return null
    const chosen = f.crossSell ? answers.value[f.crossSell.answerKey] : undefined
    if (chosen === 'security' || chosen === 'indispensable') {
      const upsell = base.upsellBundles.find((u) => f.crossSellValueOf(u) === chosen)
      if (upsell) return upsell
    }
    return base
  })

  const priceBundleId = computed<string | null>(() => selectedBundle.value?.id ?? null)

  /** i18n key (in the `bundles` namespace) for the chosen bundle's translated
   *  label — the combined "<formula> + <add-on>" when an add-on is selected. */
  const selectedBundleKey = computed<string | null>(() => selectedBundle.value?.key ?? null)

  /** Does the chosen bundle have automatic Assia forwarding? Derived fact of the
   *  bundle (symmetric to selectedBundleKey); interpreted by useSubscriptionOffer
   *  to gate the RIB + NIR collection. */
  const assiaForwardingAutomatic = computed(() => selectedBundle.value?.assiaForwarding === 'automatic')

  // compute_price params: the period (bundle-independent) plus the bundle id.
  const planPriceParams = computed<ComputePriceParams | null>(() =>
    priceBundleId.value && period.value ? { bundleId: priceBundleId.value, ...period.value } : null,
  )
  const tonicPriceParams = computed<ComputePriceParams | null>(() => {
    const id = topUpBundle.value?.id
    return topUpSubscribed.value && id && period.value ? { bundleId: id, ...period.value } : null
  })

  // Contracts to finalize at checkout: the chosen formula plus, when subscribed,
  // the top-up — each a bundle over the selected period. Empty until the bundle
  // and period are resolved. compute_price params already shape exactly
  // { bundleId, effectiveFrom, effectiveUntil }.
  const contractInputs = computed<ContractInput[]>(() => {
    const out: ContractInput[] = []
    if (planPriceParams.value) out.push({ ...planPriceParams.value })
    if (tonicPriceParams.value) out.push({ ...tonicPriceParams.value })
    return out
  })

  // Headline total for the chosen period: formula (+ top-up when subscribed),
  // both computed by the backend. Null until every needed total is in.
  const subscriptionTotal = computed<number | null>(() => {
    if (planTotal.amount.value == null) return null
    let total = planTotal.amount.value
    if (topUpSubscribed.value) {
      if (topUpTotal.amount.value == null) return null
      total += topUpTotal.amount.value
    }
    return round2(total)
  })
  // Per-product totals over the chosen period (used by the final recap to price
  // each card on its own): the formula (base or combined upsell) and the top-up.
  const planPeriodTotal = computed<number | null>(() => planTotal.amount.value)
  const tonicPeriodTotal = computed<number | null>(() =>
    topUpSubscribed.value ? topUpTotal.amount.value : null,
  )
  const subscriptionTotalLoading = computed(
    () => planTotal.loading.value || (topUpSubscribed.value && topUpTotal.loading.value),
  )
  const subscriptionTotalError = computed(
    () => planTotal.error.value ?? (topUpSubscribed.value ? topUpTotal.error.value : null),
  )

  // Each consumer watches the derived inputs and delegates to the de-duplicated
  // loaders, keeping the shared state in sync without duplicate requests.
  watch(bundlesParams, (p) => ensureBundles(p), { immediate: true })
  watch(topUpConfig, (c) => ensureTopUp(c), { immediate: true })
  watch(planPriceParams, (p) => planTotal.ensure(p), { immediate: true })
  watch(tonicPriceParams, (p) => topUpTotal.ensure(p), { immediate: true })

  /** Retry the bundles request (used by the results-screen error state). */
  function reload(): void {
    loadedBundlesKey = null
    ensureBundles(bundlesParams.value)
  }

  return {
    hasFlow,
    products,
    crossSellAddons,
    selectedPlanPrice,
    selectedBundleKey,
    assiaForwardingAutomatic,
    tonicPrice,
    tonicLabelKey,
    lines,
    monthlyTotal,
    yearlyTotal,
    loading,
    error,
    reload,
    subscriptionTotal,
    subscriptionTotalLoading,
    subscriptionTotalError,
    planPeriodTotal,
    tonicPeriodTotal,
    contractInputs,
  }
}
